import { runConversation } from '@pypestream/testview/browser';

import type { Channel } from './get-channels-from-pypes';
import { buildEngagementApiUrl } from './engagement-api-url';
import { listTestChannels } from '../components/test-surface/select-test-channel';

/**
 * Project Bobcat — after a solution that calls models through the AI Gateway
 * is published to Sandbox, one conversation is sent to it in the background,
 * so the slow first call after a publish (the action container and the
 * gateway warming up; the engine times it out and retries, ~80 s seen on
 * candidate) happens before anyone tests it.
 *
 * Sandbox only: a warm-up on Live would add a session to the client's
 * production analytics. It is a real conversation, so it shows in Sandbox
 * analytics like a test chat; the message asks what the bot can do, which
 * the Knowledge Agent answers and nothing hands to a person.
 */

export const WARM_UP_MESSAGE = 'What can you help me with?';

/** Long enough for the published version to be the one that answers. */
export const WARM_UP_DELAY_MS = 15_000;

/** The scripts that call the AI Gateway (`gateway-models.ts`). */
const GATEWAY_SCRIPTS = /(^|\/)(CallAIModel|CallAIClassifier)\.py$/;

/** Whether a solution's files include a script that calls the gateway. */
export const callsGateway = (files: readonly string[] | undefined): boolean =>
  (files ?? []).some((f) => GATEWAY_SCRIPTS.test(f));

/** The Sandbox web channel the warm-up talks to, if the solution has one. */
export const warmUpChannelId = (
  channels: Channel[] | null | undefined
): string | null =>
  listTestChannels(channels).find((c) => c.channelEnv === 'sandbox')?.appId ??
  null;

/**
 * Wait, then hold one conversation with the channel. Resolves either way: a
 * warm-up that fails changes nothing for the person who published.
 */
export const warmUpSandbox = async ({
  appId,
  origin = window.location.origin,
  delayMs = WARM_UP_DELAY_MS,
  run = runConversation,
  wait = (ms: number) => new Promise((r) => setTimeout(r, ms)),
}: {
  appId: string;
  origin?: string;
  delayMs?: number;
  run?: typeof runConversation;
  wait?: (ms: number) => Promise<unknown>;
}): Promise<boolean> => {
  await wait(delayMs);
  try {
    await run({
      appId,
      engagementApiUrl: buildEngagementApiUrl(origin, 'sandbox'),
      messages: [WARM_UP_MESSAGE],
    });
    return true;
  } catch {
    // The reply can outlast the client's wait; the engine still finishes
    // the call, which is what warms it.
    return false;
  }
};
